import { Component, inject, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Subject, takeUntil, switchMap, of } from 'rxjs';

import { UsersService } from '../../service/entity-services/users.service';
import { User } from '../../objects/user';
import { EntityType } from '../../objects/interface-library';
import { ViewEntity } from '../../objects/view-entity';
import { NewAttachmentComponent } from '../../create-new/new-attachment/new-attachment.component';
import { EntityActionsSidenavComponent, EntityAction, EntityTab } from '../../entity-form-components/entity-actions-sidenav/entity-actions-sidenav.component';
import { BaaConnectionsComponent } from '../../entity-form-components/baa-connections/baa-connections.component';
import { CoiConnectionsComponent } from '../../entity-form-components/coi-connections/coi-connections.component';
import { VendorConnectionsComponent } from '../../entity-form-components/vendor-connections/vendor-connections.component';
import { AttachmentConnectionsComponent } from '../../entity-form-components/attachment-connections/attachment-connections.component';
import { CriticalDateConnectionsComponent } from '../../entity-form-components/critical-date-connections/critical-date-connections.component';

@Component({
  selector: 'app-user',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatChipsModule,
    MatTabsModule,
    MatSidenavModule,
    MatToolbarModule,
    EntityActionsSidenavComponent,
    BaaConnectionsComponent,
    CoiConnectionsComponent,
    VendorConnectionsComponent,
    AttachmentConnectionsComponent,
    CriticalDateConnectionsComponent
  ],
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.css']
})
export class UserComponent extends ViewEntity implements OnInit, OnDestroy {
  public route = inject(ActivatedRoute);
  public router = inject(Router);
  public usersService = inject(UsersService);
  private destroy$ = new Subject<void>();

  currentUser: User | null = null;
  loading = true;
  error: string | null = null;

  // Display-friendly values
  departmentsDisplay: string = '';
  sitesDisplay: string = '';
  phonesDisplay: string = '';

  // Tab management
  selectedTabIndex = 0;

  // Sidenav actions using signals (Angular 20 pattern)
  sidenavActions = signal<EntityAction[]>([
    {
      label: 'Edit',
      icon: 'edit',
      action: () => this.editUser(),
      disabled: false
    },
    {
      label: 'Add Attachment',
      icon: 'attach_file',
      action: () => this.addAttachment(),
      disabled: false
    },
    {
      label: 'New Important Date',
      icon: 'event',
      action: () => this.addCriticalDate(),
      disabled: false
    }
  ]);

  // Tabs configuration using signals
  sidenavTabs = signal<EntityTab[]>([
    { label: 'Details', index: 0, icon: 'info' },
    // { label: 'Contracts', index: 1, icon: 'description' },
    // { label: 'BAAs', index: 2, icon: 'policy' },
    // { label: 'COIs', index: 3, icon: 'verified_user' },
    // { label: 'Vendors', index: 4, icon: 'business' },
    // { label: 'Parties', index: 5, icon: 'groups' },
    // { label: 'People', index: 6, icon: 'person' },
    { label: 'Attachments', index: 1, icon: 'attach_file' },
    { label: 'Dates', index: 2, icon: 'event' },
    // { label: 'Notes', index: 9, icon: 'note' },
    // { label: 'Risk Assessments', index: 10, icon: 'assessment' }
  ]);

  ngOnInit() {
    const userNum = this.route.snapshot.paramMap.get('id');

    if (!userNum) {
      this.error = 'No user ID provided';
      this.loading = false;
      return;
    }

    this.loadUserById(userNum);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadUserById(userId: string) {
    this.loading = true;
    this.error = null;

    // Get the user from the allUsers$ observable (already loaded from Cognito/SSO)
    this.usersService.allUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users) => {
          // Try to find user by email (primary identifier)
          const user = users.find((u: any) =>
            u.email === userId ||
            u.userId === userId ||
            u.userNum === userId
          );

          if (user) {
            this.currentUser = user;
            this.processUserData();
            this.loading = false;
          } else {
            this.error = 'User not found in users list';
            this.loading = false;
          }
        },
        error: (err) => {
          console.error('Error loading user:', err);
          this.error = err.message || 'Failed to load user. Please try again.';
          this.loading = false;
        }
      });
  }

  processUserData() {
    if (!this.currentUser) return;

    // Process departments array
    if (this.currentUser.departments && Array.isArray(this.currentUser.departments)) {
      this.departmentsDisplay = this.currentUser.departments.join(', ');
    } else {
      this.departmentsDisplay = 'N/A';
    }

    // Process sites array (extract names from objects)
    if (this.currentUser.sites && Array.isArray(this.currentUser.sites)) {
      const siteNames = this.currentUser.sites
        .map(site => {
          if (typeof site === 'string') return site;
          if (typeof site === 'object' && site.name) return site.name;
          if (typeof site === 'object' && site.siteName) return site.siteName;
          return '';
        })
        .filter(name => name);
      this.sitesDisplay = siteNames.length > 0 ? siteNames.join(', ') : 'N/A';
    } else {
      this.sitesDisplay = 'N/A';
    }

    // Process phone numbers
    const phones: string[] = [];
    if (this.currentUser.officePhone) phones.push(`Office: ${this.currentUser.officePhone}`);
    if (this.currentUser.mobilePhone) phones.push(`Mobile: ${this.currentUser.mobilePhone}`);
    if (this.currentUser.faxPhone) phones.push(`Fax: ${this.currentUser.faxPhone}`);
    this.phonesDisplay = phones.length > 0 ? phones.join(', ') : 'N/A';

    // Load critical dates associated with this user
    if (this.currentUser.criticalDates && this.currentUser.criticalDates.length > 0) {
      this.criticalDatesService.getDatesByNumbers(this.currentUser.criticalDates)
        .subscribe(dates => {
          this.entityDates = dates;
        });
    } else {
      this.entityDates = [];
    }

    // Load attachments associated with this user
    // NEW API (Phase 1): Query by parentEntityId, with fallback to legacy attachment IDs array
    const userId = this.currentUser.userNum || this.currentUser.userId;
    if (userId) {
      this.attachmentsService.getAttachmentsForEntity(userId, 'User')
        .subscribe(attachments => {
          if (attachments && attachments.length > 0) {
            this.entityAttachments = attachments;
          } else if (this.currentUser?.attachments?.length) {
            // Fallback: ETL'd users may not have parentEntityId on attachments
            this.attachmentsService.getAttachmentsByNumbers(this.currentUser.attachments)
              .subscribe(legacyAttachments => {
                this.entityAttachments = legacyAttachments || [];
              });
          } else {
            this.entityAttachments = [];
          }
        });
    } else {
      this.entityAttachments = [];
    }
  }

  editUser() {
    // Try multiple identifier fields since users may not have userNum populated
    const userId = this.currentUser?.userNum || this.currentUser?.userId || this.currentUser?.email;
    if (userId) {
      this.router.navigate(['/securehome/new-user', userId]);
    } else {
      console.error('No user identifier found for edit navigation');
    }
  }

  goBack() {
    this.router.navigate(['/securehome/users']);
  }

  // Tab selection handler
  selectTab(index: number): void {
    this.selectedTabIndex = index;
  }

  // Action methods
  addAttachment(): void {
    const entityObjectForDialog = {
      ...this.currentUser,
      entityType: EntityType.User
    };

    const dialogRef = this.dialog.open(NewAttachmentComponent, {
      width: '500px',
      disableClose: true,
      data: {
        entityObject: entityObjectForDialog
      }
    });

    dialogRef.afterClosed().subscribe(newAttachment => {
      if (!this.currentUser) return;

      if (newAttachment && newAttachment.attachmentNum) {
        const userId = this.currentUser.userNum || this.currentUser.userId;
        if (userId) {
          this.attachmentsService.getAttachmentsForEntity(userId, 'User')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (attachments) => {
                this.entityAttachments = attachments;
              },
              error: (err) => {
                console.error('Failed to fetch attachments:', err);
                this.entityAttachments = [...this.entityAttachments, newAttachment];
              }
            });
        }
      }
    });
  }

  addCriticalDate(): void {
    // Use the base class method from ViewEntity
    super['addCriticalDate'](
      this.currentUser,
      'userNum',
      (id: string, update: any) => {
        // Extract the attribute name and value from the update object
        const attribute = Object.keys(update)[0];
        const value = update[attribute];
        // Note: You may need to implement updateUserAttribute in UsersService
        // For now, this will log an error if the method doesn't exist
        return of(null); // Placeholder - replace with actual service call
      }
    );
  }

  refreshAttachments(): void {
    const userId = this.currentUser?.userNum || this.currentUser?.userId;
    if (userId) {
      this.attachmentsService.getAttachmentsForEntity(userId, 'User')
        .pipe(takeUntil(this.destroy$))
        .subscribe(attachments => {
          if (attachments && attachments.length > 0) {
            this.entityAttachments = attachments;
          } else if (this.currentUser?.attachments?.length) {
            this.attachmentsService.getAttachmentsByNumbers(this.currentUser.attachments)
              .pipe(takeUntil(this.destroy$))
              .subscribe(legacyAttachments => {
                this.entityAttachments = legacyAttachments || [];
              });
          } else {
            this.entityAttachments = [];
          }
        });
    }
  }

  refreshCriticalDates(): void {
    if (this.currentUser?.criticalDates && this.currentUser.criticalDates.length > 0) {
      this.criticalDatesService.getDatesByNumbers(this.currentUser.criticalDates)
        .pipe(takeUntil(this.destroy$))
        .subscribe(dates => {
          this.entityDates = dates;
        });
    }
  }
}
